import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MfaChallenge, MfaParams } from '../../../../shared/objects/auth/mfa';
import { LoginStatusEnum } from '../../../../shared/objects/enum/loginStatus';

/**
 * Second login step, shown after a correct password: either set up an authenticator app (QR code)
 * or enter its code. New recovery codes are shown first, whenever Keycloak issues them.
 */
@Component({
  selector: 'app-login-mfa',
  templateUrl: './login-mfa.component.html',
  styleUrls: ['./login-mfa.component.scss'],
  standalone: false,
})
export class LoginMfaComponent implements OnChanges {
  @Input() challenge: MfaChallenge;
  @Input() status: LoginStatusEnum;
  @Input() username: string;
  @Output() verify = new EventEmitter<MfaParams>();
  @Output() cancel = new EventEmitter<void>();

  Status = LoginStatusEnum;
  code = '';
  recoveryCode = '';
  useRecoveryCode = false;
  rememberDevice = false;
  codesSaved = false;
  codesSavedConfirmed = false;
  codesCopied = false;
  /** Recovery code accepted by Keycloak, re-sent while setting up the replacement authenticator. */
  private acceptedRecoveryCode: string = null;

  get isSetup(): boolean {
    return (
      this.challenge?.error === 'mfa_setup_required' && !!this.challenge.totp
    );
  }

  get newRecoveryCodes(): string[] {
    return this.challenge?.recovery_codes ?? [];
  }

  get formattedRecoveryCodes(): string[] {
    return this.newRecoveryCodes.map(c => c.match(/.{1,4}/g).join('-'));
  }

  get showRecoveryCodes(): boolean {
    return this.newRecoveryCodes.length > 0 && !this.codesSaved;
  }

  get canUseRecoveryCode(): boolean {
    return !this.isSetup && this.challenge?.recovery_codes_remaining > 0;
  }

  get trustedDeviceDays(): number {
    return this.challenge?.trusted_device_days ?? 0;
  }

  get busy(): boolean {
    return this.status === LoginStatusEnum.WAITING;
  }

  get canSubmit(): boolean {
    if (this.busy) {
      return false;
    }
    if (this.useRecoveryCode) {
      return this.recoveryCode.replace(/[\s-]/g, '').length >= 8;
    }
    return /^\d{6,8}$/.test(this.code.replace(/\s/g, ''));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const challenge = changes.challenge;
    if (challenge && challenge.currentValue !== challenge.previousValue) {
      this.code = '';
      this.codesSaved = false;
      this.codesSavedConfirmed = false;
      this.codesCopied = false;
      if (this.isSetup && this.useRecoveryCode) {
        this.acceptedRecoveryCode = this.recoveryCode.trim();
        this.useRecoveryCode = false;
      } else if (!this.isSetup) {
        this.acceptedRecoveryCode = null;
      }
    }
    if (changes.status && this.status === LoginStatusEnum.INVALID_MFA_CODE) {
      this.code = '';
    }
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }
    if (this.useRecoveryCode) {
      this.verify.emit({ recovery_code: this.recoveryCode.trim() });
      return;
    }
    const params: MfaParams = {
      totp: this.code.replace(/\s/g, ''),
      remember_device: this.rememberDevice,
    };
    if (this.isSetup) {
      params.totp_secret = this.challenge.totp.secret;
      if (this.acceptedRecoveryCode) {
        params.recovery_code = this.acceptedRecoveryCode;
      }
    }
    if (this.newRecoveryCodes.length) {
      params.recovery_codes = this.newRecoveryCodes.join(',');
    }
    this.verify.emit(params);
  }

  toggleRecoveryCode(): void {
    this.useRecoveryCode = !this.useRecoveryCode;
    this.recoveryCode = '';
    this.code = '';
  }

  copyRecoveryCodes(): void {
    navigator.clipboard
      ?.writeText(this.recoveryCodesText())
      .then(() => (this.codesCopied = true));
  }

  downloadRecoveryCodes(): void {
    const blob = new Blob([this.recoveryCodesText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'carrier-portal-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  private recoveryCodesText(): string {
    return [
      `Carrier Portal recovery codes for ${this.username}`,
      `Generated ${new Date().toISOString().substring(0, 10)}. Each code works once, in any order.`,
      '',
      ...this.formattedRecoveryCodes.map((c, i) => `${i + 1}. ${c}`),
      '',
    ].join('\n');
  }
}
